import { Users, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { useCreateGroup, useOpenDirect } from '../api'
import type { Contact } from '../types'
import { ContactPicker } from './ContactPicker'

interface NewConversationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  base: string
  /** Staff may create groups; students and guardians write to one person. */
  canGroup: boolean
}

/** Pick one person (direct conversation) or several with a name (group, staff only). */
export function NewConversationDialog({ open, onOpenChange, base, canGroup }: NewConversationDialogProps) {
  const { t } = useTranslation('messaging')
  const navigate = useNavigate()
  const [group, setGroup] = useState(false)
  const [picked, setPicked] = useState<Contact[]>([])
  const [title, setTitle] = useState('')
  const direct = useOpenDirect()
  const create = useCreateGroup()

  const close = () => {
    onOpenChange(false)
    setGroup(false)
    setPicked([])
    setTitle('')
  }

  const openDirect = (c: Contact) =>
    direct.mutate(c.id, {
      onSuccess: (conversation) => {
        close()
        void navigate(`${base}/${conversation.id}`)
      },
      onError: (e) => toast.error(t('Could not open the conversation'), toApiError(e).message),
    })

  const createGroup = () =>
    create.mutate(
      { title: title.trim(), userIds: picked.map((p) => p.id) },
      {
        onSuccess: (conversation) => {
          close()
          void navigate(`${base}/${conversation.id}`)
        },
        onError: (e) => toast.error(t('Could not create the group'), toApiError(e).message),
      },
    )

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => (o ? onOpenChange(true) : close())}
      title={group ? t('New group') : t('New conversation')}
      description={group ? t('Choose the people and a name for the group.') : t('Choose who to write to.')}
      size="md"
      footer={
        group ? (
          <>
            <Button variant="secondary" onClick={() => setGroup(false)}>
              {t('Back')}
            </Button>
            <Button
              onClick={createGroup}
              loading={create.isPending}
              disabled={picked.length === 0 || title.trim() === ''}
            >
              {t('Create the group')}
            </Button>
          </>
        ) : undefined
      }
    >
      {group ? (
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-ink">{t('Group name')}</span>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder={t('e.g. Grade 5 teachers')}
            />
          </label>
          {picked.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5" aria-label={t('Chosen people')}>
              {picked.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center gap-1 rounded-full bg-brand-50 py-0.5 pr-1 pl-2.5 text-xs text-brand-800"
                >
                  {p.name}
                  <button
                    type="button"
                    onClick={() => setPicked((all) => all.filter((x) => x.id !== p.id))}
                    className="grid size-4 place-items-center rounded-full hover:bg-brand-100"
                    aria-label={t('Remove {{name}}', { name: p.name })}
                  >
                    <X className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <ContactPicker
            multiple
            selected={picked}
            onToggle={(c) =>
              setPicked((all) =>
                all.some((x) => x.id === c.id) ? all.filter((x) => x.id !== c.id) : [...all, c],
              )
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {canGroup ? (
            <button
              type="button"
              onClick={() => setGroup(true)}
              className="flex w-full items-center gap-3 rounded-control border border-dashed border-line-strong px-3 py-2.5 text-left text-sm font-medium text-brand-700 hover:bg-brand-50"
            >
              <Users className="size-4" aria-hidden="true" />
              {t('Create a group')}
            </button>
          ) : null}
          <ContactPicker multiple={false} selected={[]} onToggle={openDirect} />
        </div>
      )}
    </Dialog>
  )
}
